/**
 * Email validation utilities for signup bot protection.
 *
 * Two checks:
 *   1. Disposable/temporary email domain blocklist (~120K domains)
 *   2. Basic format sanity (redundant with browser `type="email"` but
 *      belt-and-suspenders for programmatic callers)
 *
 * These run client-side for instant UX feedback. The server-side gate is
 * Supabase's Turnstile integration (configured in dashboard) which blocks
 * automated signups even if they bypass our frontend checks.
 */

// The package exports a JSON array of ~120K disposable domains.
// Import as a Set for O(1) lookups.
import disposableDomains from 'disposable-email-domains';

const DISPOSABLE_DOMAIN_SET: Set<string> = new Set(disposableDomains);

/**
 * Returns an error message if the email is invalid for signup purposes,
 * or null if it passes all checks.
 */
export function validateSignupEmail(email: string): string | null {
  const trimmed = email.trim().toLowerCase();

  if (!trimmed || !trimmed.includes('@')) {
    return 'Please enter a valid email address.';
  }

  const domain = trimmed.split('@').pop();
  if (!domain || !domain.includes('.')) {
    return 'Please enter a valid email address.';
  }

  if (DISPOSABLE_DOMAIN_SET.has(domain)) {
    return 'Temporary email addresses are not allowed. Please use a permanent email.';
  }

  return null;
}
